import axios from '../utils/axios';
import { devLogApiError } from '../utils/devLogApiError';
import { apiRejectPayload } from '../utils/apiRejectPayload';
import { isAnalyticsAllowed, onLevelsChanged } from '../utils/cookieConsentLevels';
import { isShopHybridSeoProject } from '../utils/shopSeoProjectContour';

const IS_DEV = process.env.NODE_ENV === 'development';

function logSeoDevApiError(context, error) {
  devLogApiError(context, error);
}

/**
 * Сервис для работы с SEO настройками
 */
class SeoService {
  constructor() {
    // ✅ Защита от повторной инициализации аналитики/скриптов
    this._analyticsInitPromise = null;
    this._analyticsInitialized = false;
    // ✅ CMP-gate: подписка на изменение cookie-согласий (152-ФЗ ст. 9 ч. 4)
    // Загрузка Метрики/GA/GTM откладывается до явного opt-in уровня analytics;
    // когда пользователь даёт согласие через CookieBanner или Privacy Center,
    // эта подписка повторно запускает initAnalytics().
    this._consentUnsubscribe = null;
    // ✅ Кэш для мета-тегов (предотвращает массовые запросы)
    this._metaCache = new Map();
    this._cacheTimeout = 5 * 60 * 1000; // 5 минут
  }
  /**
   * Получить список всех проектов
   */
  async getProjects() {
    try {
      // App /admin/seo = platform contour only (vde76 / shop-* stay in SHOP admin).
      const response = await axios.get('/api/seo/v1/projects', {
        params: { contour: 'platform' },
      });
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: получение проектов', error);
      throw error;
    }
  }

  /**
   * Получить настройки проекта по slug
   */
  async getProjectSettings(projectSlug) {
    try {
      if (isShopHybridSeoProject(projectSlug)) {
        const err = new Error(
          'SEO витрины настраивается только в SHOP admin на домене магазина, не в App /admin/seo.'
        );
        err.code = 'STOREFRONT_SEO_CONTOUR_FORBIDDEN';
        throw err;
      }
      const response = await axios.get(`/api/seo/v1/admin/${projectSlug}/settings`);
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: настройки проекта', error);
      throw error;
    }
  }

  /**
   * Обновить настройки проекта по slug
   */
  async updateProjectSettings(projectSlug, settings) {
    try {
      const response = await axios.put(`/api/seo/v1/admin/${projectSlug}/settings`, settings);
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: обновление настроек проекта', error);
      throw error;
    }
  }

  /**
   * Автообнаружение проектов
   */
  async discoverProjects() {
    try {
      const response = await axios.post('/api/seo/v1/projects/discover');
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: автообнаружение проектов', error);
      throw error;
    }
  }

  /**
   * Создать новый проект
   */
  async createProject(projectData) {
    try {
      const response = await axios.post('/api/seo/v1/projects', projectData);
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: создание проекта', error);
      throw error;
    }
  }

  async getSitemapUrls(projectSlug, filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.is_active !== undefined) params.set('is_active', String(filters.is_active));
      const qs = params.toString();
      const response = await axios.get(
        `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/sitemap-urls${qs ? `?${qs}` : ''}`
      );
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: sitemap URLs', error);
      throw error;
    }
  }

  async createSitemapUrl(projectSlug, payload) {
    try {
      const response = await axios.post(
        `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/sitemap-urls`,
        payload
      );
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: создание sitemap URL', error);
      throw error;
    }
  }

  async updateSitemapUrl(projectSlug, id, payload) {
    try {
      const response = await axios.put(
        `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/sitemap-urls/${encodeURIComponent(id)}`,
        payload
      );
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: обновление sitemap URL', error);
      throw error;
    }
  }

  async deleteSitemapUrl(projectSlug, id) {
    try {
      const response = await axios.delete(
        `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/sitemap-urls/${encodeURIComponent(id)}`
      );
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: удаление sitemap URL', error);
      throw error;
    }
  }

  async getIntents(projectSlug, routePath) {
    try {
      const params = routePath ? { route_path: routePath } : {};
      const response = await axios.get(
        `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/intents`,
        { params }
      );
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: intent map', error);
      throw error;
    }
  }

  async createIntent(projectSlug, payload) {
    const response = await axios.post(
      `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/intents`,
      payload
    );
    return response.data;
  }

  async updateIntent(projectSlug, intentId, payload) {
    const response = await axios.put(
      `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/intents/${encodeURIComponent(intentId)}`,
      payload
    );
    return response.data;
  }

  async deleteIntent(projectSlug, intentId) {
    const response = await axios.delete(
      `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/intents/${encodeURIComponent(intentId)}`
    );
    return response.data;
  }

  async importIntents(projectSlug, intents) {
    const response = await axios.post(
      `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/intents/import`,
      { intents }
    );
    return response.data;
  }

  async exportIntents(projectSlug) {
    const response = await axios.get(
      `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/intents/export`
    );
    return response.data;
  }

  async previewApplyIntents(projectSlug) {
    const response = await axios.post(
      `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/intents/apply-preview`
    );
    return response.data;
  }

  async applyIntents(projectSlug, preview) {
    const response = await axios.post(
      `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/intents/apply`,
      preview ? { preview } : {}
    );
    return response.data;
  }

  async getProjectHealth(projectSlug) {
    const response = await axios.get(
      `/api/seo/v1/admin/${encodeURIComponent(projectSlug)}/health`
    );
    return response.data;
  }

  /**
   * Получить глобальные SEO настройки
   */
  async getSettings() {
    try {
      const response = await axios.get('/api/seo/settings');
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: получение SEO-настроек', error);
      throw error;
    }
  }

  /**
   * Обновить глобальные SEO настройки
   */
  async updateSettings(settings) {
    try {
      const response = await axios.put('/api/seo/settings', settings);
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: обновление SEO-настроек', error);
      throw error;
    }
  }

  /**
   * Загрузить favicon (file: File)
   */
  async uploadFavicon(file) {
    const form = new FormData();
    form.append('file', file);
    const res = await axios.post('/api/seo/upload/favicon', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  }

  /**
   * Загрузить логотип публичного сайта (file: File)
   */
  async uploadSiteLogo(file) {
    const form = new FormData();
    form.append('file', file);
    const res = await axios.post('/api/seo/upload/site-logo', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  }

  /**
   * Удалить favicon
   */
  async deleteFavicon() {
    try {
      const response = await axios.delete('/api/seo/delete/favicon');
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: удаление favicon', error);
      throw error;
    }
  }

  /**
   * Удалить логотип публичного сайта
   */
  async deleteSiteLogo() {
    try {
      const response = await axios.delete('/api/seo/delete/site-logo');
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: удаление логотипа сайта', error);
      throw error;
    }
  }

  /**
   * Получить список SEO страниц
   */
  async getPages(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.page_type) params.append('page_type', filters.page_type);
      if (filters.is_active !== undefined) params.append('is_active', filters.is_active);

      const response = await axios.get(`/api/seo/pages?${params.toString()}`);
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: список SEO-страниц', error);
      throw error;
    }
  }

  /**
   * Получить конкретную SEO страницу
   */
  async getPage(id) {
    try {
      const response = await axios.get(`/api/seo/pages/${id}`);
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: одна SEO-страница', error);
      throw error;
    }
  }

  /**
   * Создать новую SEO страницу
   */
  async createPage(pageData) {
    try {
      const response = await axios.post('/api/seo/pages', pageData);
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: создание SEO-страницы', error);
      throw error;
    }
  }

  /**
   * Обновить SEO страницу
   */
  async updatePage(id, pageData) {
    try {
      const response = await axios.put(`/api/seo/pages/${id}`, pageData);
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: обновление SEO-страницы', error);
      throw error;
    }
  }

  /**
   * Удалить SEO страницу
   */
  async deletePage(id) {
    try {
      const response = await axios.delete(`/api/seo/pages/${id}`);
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: удаление SEO-страницы', error);
      throw error;
    }
  }

  /**
   * Получить мета-теги для страницы
   * ✅ КЭШИРОВАНИЕ: предотвращает массовые запросы при повторных вызовах
   */
  async getMetaTags(pageType, entityId = null) {
    const cacheKey = `${pageType}:${entityId || 'null'}`;
    const cached = this._metaCache.get(cacheKey);
    
    // Проверяем кэш
    if (cached && Date.now() - cached.timestamp < this._cacheTimeout) {
      if (IS_DEV) {
        console.log(`📦 SEO кэш: используем закэшированные мета-теги для ${cacheKey}`);
      }
      return cached.data;
    }

    try {
      const params = entityId ? `?entityId=${entityId}` : '';
      const response = await axios.get(`/api/seo/meta/${pageType}${params}`);
      
      // Сохраняем в кэш
      this._metaCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });
      
      // Очищаем старые записи из кэша (старше 10 минут)
      const now = Date.now();
      for (const [key, value] of this._metaCache.entries()) {
        if (now - value.timestamp > 10 * 60 * 1000) {
          this._metaCache.delete(key);
        }
      }
      
      return response.data;
    } catch (error) {
      logSeoDevApiError('SEO: мета-теги', error);
      throw error;
    }
  }

  /**
   * Установить мета-теги для текущей страницы
   */
  setMetaTags(metaData) {
    if (!metaData) return;

    const ogTitle = metaData.og_title || metaData.title;
    const ogDescription = metaData.og_description || metaData.description;
    if (metaData.title) {
      document.title = metaData.title;
    }

    // Устанавливаем description
    this.setMetaTag('name', 'description', metaData.description);


    // Устанавливаем keywords
    if (metaData.keywords) {
      this.setMetaTag('name', 'keywords', metaData.keywords);
    }

    // Устанавливаем robots
    if (metaData.robots) {
      const robotsContent = [];
      if (metaData.robots.noindex) robotsContent.push('noindex');
      if (metaData.robots.nofollow) robotsContent.push('nofollow');
      if (robotsContent.length > 0) {
        this.setMetaTag('name', 'robots', robotsContent.join(', '));
      }
    }

    // Canonical устанавливается ниже (с fallback на текущий URL)

    // Устанавливаем verification коды
    if (metaData.verification) {
      if (metaData.verification.google) {
        this.setMetaTag('name', 'google-site-verification', metaData.verification.google);
      }
      if (metaData.verification.yandex) {
        this.setMetaTag('name', 'yandex-verification', metaData.verification.yandex);
      }
      if (metaData.verification.bing) {
        this.setMetaTag('name', 'msvalidate.01', metaData.verification.bing);
      }
    }

    // ✅ Региональность для Яндекс Вебмастера (geo.region)
    if (metaData.geo_region) {
      this.setMetaTag('name', 'geo.region', metaData.geo_region);
    }

    // ✅ Canonical: если не передан — берём текущий URL (без hash) для корректной индексации
    const canonicalUrl = metaData.canonical || (typeof window !== 'undefined' && window.location.origin && window.location.pathname
      ? `${window.location.origin}${window.location.pathname}${window.location.search || ''}`
      : null);
    if (canonicalUrl) {
      this.setLinkTag('canonical', canonicalUrl);
    }

    // ✅ Open Graph (Facebook, ВК, Telegram, часть AI-краулеров)
    if (ogTitle) {
      this.setMetaTag('property', 'og:title', ogTitle);
      this.setMetaTag('property', 'og:type', metaData.og_type || 'website');
    }
    if (ogDescription) {
      this.setMetaTag('property', 'og:description', ogDescription);
    }
    if (canonicalUrl) {
      this.setMetaTag('property', 'og:url', canonicalUrl);
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
      this.setMetaTag('property', 'og:site_name', metaData.og_site_name || metaData.title || 'MarkBase');
    }
    this.setMetaTag('property', 'og:locale', metaData.og_locale || 'ru_RU');
    const ogImage = metaData.og_image || (metaData.branding?.site_logo_url && typeof window !== 'undefined'
      ? (metaData.branding.site_logo_url.startsWith('http')
        ? metaData.branding.site_logo_url
        : `${window.location.origin}${metaData.branding.site_logo_url}`)
      : null);
    if (ogImage) {
      this.setMetaTag('property', 'og:image', ogImage);
    }

    // ✅ Twitter Card (поиск и нейросети, использующие Twitter-метаданные)
    this.setMetaTag('name', 'twitter:card', metaData.twitter_card || 'summary_large_image');
    if (metaData.twitter_site) {
      this.setMetaTag('name', 'twitter:site', metaData.twitter_site);
    }
    if (metaData.twitter_creator) {
      this.setMetaTag('name', 'twitter:creator', metaData.twitter_creator);
    }
    if (ogTitle) {
      this.setMetaTag('name', 'twitter:title', ogTitle);
    }
    if (ogDescription) {
      this.setMetaTag('name', 'twitter:description', ogDescription);
    }
    if (ogImage) {
      this.setMetaTag('name', 'twitter:image', ogImage);
    }

    // Добавляем JSON-LD (Организация/Сайт/Приложение/Вопросы) для поисковых систем и нейросетей
    try {
      const branding = metaData.branding || {};
      const siteName = branding.site_name || metaData.title || document.title || '';
      const siteDescription = metaData.description || '';

      const canonicalLink = document.querySelector('link[rel="canonical"]');
      const pageUrl = (canonicalLink && canonicalLink.href) || (window?.location?.href || '');

      const jsonLd = [];
      // Организация
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteName,
        url: pageUrl,
        logo: branding.site_logo_url || undefined
      });
      // Сайт
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: siteName,
        url: pageUrl,
        inLanguage: 'ru-RU',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${window?.location?.origin || ''}/?q={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      });
      // Приложение (описание платформы)
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: siteName,
        operatingSystem: 'Веб',
        applicationCategory: 'Бизнес-приложение',
        description: siteDescription,
        inLanguage: 'ru-RU',
        featureList: [
          'Единый каталог товаров, услуг и составных предложений',
          'Управление заказами, складами, доставкой и документами',
          'Подключаемые платёжные, кассовые, ЭДО и логистические сервисы',
          'CRM, HRM, коммуникации и рабочие роли внутри компании',
          'Аналитика, финансы, тарифы и биллинговый контур',
          'ВЭЙГПТ и AI-ассистенты для работы с данными платформы',
          'Company-context, разграничение прав и изоляция данных',
          'Публичные витрины, B2B-кабинеты, Telegram и marketplace-сценарии'
        ],
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'RUB',
          description: 'Тарифы и лимиты подгружаются из биллингового контура платформы. Компания может начать с бесплатного тарифа и подключать модули по мере роста.'
        }
      });

      // Часто задаваемые вопросы
      jsonLd.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Что такое Маркбейс?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Маркбейс — это модульная B2B/B2C-платформа для торговли, заказов, складов, доставки, документов, аналитики, клиентов и AI-ассистентов. Платформа объединяет рабочие процессы компании в одном кабинете и использует единые источники данных.'
            }
          },
          {
            '@type': 'Question',
            name: 'Какие интеграции поддерживает Маркбейс?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Платформа поддерживает подключаемые сценарии для оплат и касс, ЭДО, доставки, Telegram, публичных витрин, B2B-кабинетов, marketplace и внешних каналов. Интеграции работают через настройки, права доступа и контракты платформы.'
            }
          },
          {
            '@type': 'Question',
            name: 'Сколько стоит использование Маркбейс?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Актуальные тарифы, лимиты и условия отображаются из биллингового контура платформы на публичной странице тарифов. Компания может начать с бесплатного тарифа и подключать дополнительные модули по мере необходимости.'
            }
          },
          {
            '@type': 'Question',
            name: 'Какие форматы данных поддерживает платформа?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Маркбейс работает с таблицами, каталогами, данными поставщиков, API-интеграциями и ручным вводом. Данные приводятся к структуре платформы, чтобы товары, заказы, склады, цены и документы были связаны между собой.'
            }
          },
          {
            '@type': 'Question',
            name: 'Подходит ли Маркбейс для партнёров и технических команд?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Да. Платформа описывает публичные страницы, модули, интеграции, реквизиты, юридические документы и API-сценарии так, чтобы пользователям, партнёрам и техническим специалистам было понятно, какие возможности доступны и как они подключаются.'
            }
          }
        ]
      });

      // JSON-LD из SEO Core (AEO / произвольная разметка)
      if (metaData.aeo_enabled !== false && metaData.aeo_schema_markup) {
        try {
          const raw = String(metaData.aeo_schema_markup).trim();
          if (raw) {
            const parsed = JSON.parse(raw);
            const extra = Array.isArray(parsed) ? parsed : [parsed];
            for (const item of extra) {
              if (item && typeof item === 'object') jsonLd.push(item);
            }
          }
        } catch (aeoErr) {
          if (IS_DEV) {
            console.warn('AEO schema_markup JSON не разобран:', apiRejectPayload(aeoErr).message);
          }
        }
      }

      // Удаляем старые script[type="application/ld+json"] добавленные сервисом
      document.querySelectorAll('script[data-seo-jsonld]').forEach(n => n.parentNode && n.parentNode.removeChild(n));

      // Вставляем новые
      jsonLd.forEach(obj => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', '1');
        script.text = JSON.stringify(obj);
        document.head.appendChild(script);
      });
    } catch (e) {
      if (IS_DEV) {
        console.warn('Ошибка генерации структурированных данных:', apiRejectPayload(e).message);
      }
    }
  }

  /**
   * Установить мета-тег
   */
  setMetaTag(attributeName, attributeValue, content) {
    if (!content) return;

    let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);

    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attributeName, attributeValue);
      document.head.appendChild(element);
    }

    element.setAttribute('content', content);
  }

  /**
   * Установить link тег
   */
  setLinkTag(rel, href) {
    if (!href) return;

    let element = document.querySelector(`link[rel="${rel}"]`);

    if (!element) {
      element = document.createElement('link');
      element.setAttribute('rel', rel);
      document.head.appendChild(element);
    }

    element.setAttribute('href', href);
  }

  /**
   * Установить favicon во вкладке (ico/png/svg)
   */
  setFavicon(href) {
    if (!href) return;
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'icon');
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  /**
   * Загрузить и применить мета-теги для страницы
   */
  async loadAndApplyMetaTags(pageType, entityId = null) {
    try {
      const response = await this.getMetaTags(pageType, entityId);
      if (response.success && response.data) {
        this.setMetaTags(response.data);
      }
    } catch (error) {
      logSeoDevApiError('SEO: загрузка и применение мета-тегов', error);
    }
  }

  /**
   * Инициализировать Google Analytics или Google Tag Manager
   */
  initGoogleAnalytics(trackingId) {
    if (!trackingId) return;

    // Проверяем, является ли это Google Tag Manager ID (формат GTM-XXXXXXX)
    const isGTM = /^GTM-/.test(trackingId);

    if (isGTM) {
      // Инициализируем Google Tag Manager
      if (window.google_tag_manager) {
        if (IS_DEV) console.log('✅ Google Tag Manager уже загружен');
        return;
      }

      // Загружаем GTM скрипт в head
      const gtmScript = document.createElement('script');
      gtmScript.async = true;
      gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${trackingId}`;
      document.head.appendChild(gtmScript);

      // Добавляем noscript в body
      const noscript = document.createElement('noscript');
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${trackingId}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);

      if (IS_DEV) console.log('✅ Google Tag Manager инициализирован:', trackingId);
    } else {
      // Инициализируем Google Analytics (GA4)
      // Проверяем, не загружен ли уже скрипт
      if (window.gtag) return;

      // Загружаем скрипт Google Analytics
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
      document.head.appendChild(script);

      // Инициализируем gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', trackingId);

      if (IS_DEV) console.log('✅ Google Analytics инициализирован:', trackingId);
    }
  }

  /**
   * Инициализировать Яндекс.Метрику
   * ✅ ИСПРАВЛЕНО: Правильная загрузка и инициализация скрипта
   */
  initYandexMetrika(counterId) {
    if (!counterId) {
      if (IS_DEV) console.warn('⚠️ Яндекс.Метрика: ID не указан');
      return;
    }

    // Проверяем, не инициализирована ли уже Метрика с этим ID
    if (window[`yaCounter${counterId}`]) {
      if (IS_DEV) console.log('✅ Яндекс.Метрика уже загружена для ID:', counterId);
      return;
    }

    if (IS_DEV) console.log('🔄 Загрузка Яндекс.Метрики для ID:', counterId);

    try {
      // Инициализируем функцию ym, если её ещё нет
      (function(m,e,t,r,i,k,a){
        m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
        m[i].l=1*new Date();
        for (var j = 0; j < document.scripts.length; j++) {
          if (document.scripts[j].src === r) { return; }
        }
        k=e.createElement(t);
        a=e.getElementsByTagName(t)[0];
        k.async=1;
        k.src=r;
        // ✅ Обработка ошибок загрузки скрипта (блокировщики рекламы)
        k.onerror = function() {
          if (IS_DEV) console.warn('⚠️ Яндекс.Метрика: скрипт заблокирован (возможно, блокировщиком рекламы)');
        };
        a.parentNode.insertBefore(k,a);
      })(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");

      // ✅ Обработка ошибок загрузки скрипта (блокировщики рекламы)
      // Ошибки загрузки ресурсов метрики (advert.gif) - это нормально при блокировщиках рекламы

      // Инициализируем счётчик с настройками
      // ✅ ВАЖНО: webvisor и clickmap работают только на ОДНОМ домене!
      // Если у вас РАЗНЫЕ счетчики для markbase.ru и app.markbase.ru - создайте их отдельно
      // Если ОДИН счетчик - включите "Учитывать поддомены" в настройках Яндекс.Метрики
      if (window.ym) {
        window.ym(counterId, "init", {
          clickmap: true,
          trackLinks: true,
          accurateTrackBounce: true,
          webvisor: true,
          trackHash: true,
          ecommerce: "dataLayer",
          // ✅ Для работы на поддоменах
          subdomain: true, // Учитываем поддомены
          params: {
            domain: window.location.hostname, // Явно указываем текущий домен
            isApp: window.location.hostname.startsWith('app.') // Метка для различения
          }
        });

        if (IS_DEV) console.log('✅ Яндекс.Метрика инициализирована:', counterId);
      } else {
        // Если ym не загрузилась (заблокирована), ждем немного и пробуем снова
        setTimeout(() => {
          if (window.ym && !window[`yaCounter${counterId}`]) {
            try {
              window.ym(counterId, "init", {
                clickmap: true,
                trackLinks: true,
                accurateTrackBounce: true,
                webvisor: true,
                trackHash: true,
                ecommerce: "dataLayer",
                subdomain: true,
                params: {
                  domain: window.location.hostname,
                  isApp: window.location.hostname.startsWith('app.')
                }
              });
              if (IS_DEV) console.log('✅ Яндекс.Метрика инициализирована (повторная попытка):', counterId);
            } catch (retryError) {
              if (IS_DEV) console.warn('⚠️ Яндекс.Метрика: не удалось инициализировать (возможно, заблокирована)');
            }
          }
        }, 2000);
      }
    } catch (error) {
      if (IS_DEV) {
        console.warn('⚠️ Ошибка инициализации Яндекс.Метрики (возможно, заблокирована):', apiRejectPayload(error).message);
      }
    }
  }

  /**
   * Инициализировать аналитику из публичных настроек
   * Использует публичный endpoint БЕЗ авторизации
   */
  async initAnalytics() {
    if (this._analyticsInitialized) return;
    if (this._analyticsInitPromise) return this._analyticsInitPromise;

    // ✅ CMP-gate: 152-ФЗ ст. 9 ч. 4 — analytics-class скрипты грузятся только
    // после явного opt-in пользователя (через CookieBanner или Privacy Center).
    // Если согласия нет — ждём событие изменения уровней и повторно вызываем себя.
    if (!isAnalyticsAllowed()) {
      if (IS_DEV) console.log('🛡️ SEO: analytics-cookies не разрешены, инициализация отложена до opt-in');
      if (!this._consentUnsubscribe) {
        this._consentUnsubscribe = onLevelsChanged((levels) => {
          if (levels?.analytics && !this._analyticsInitialized && !this._analyticsInitPromise) {
            if (IS_DEV) console.log('🛡️ SEO: получено согласие analytics — запускаем initAnalytics()');
            if (typeof this._consentUnsubscribe === 'function') {
              this._consentUnsubscribe();
              this._consentUnsubscribe = null;
            }
            this.initAnalytics();
          }
        });
      }
      return;
    }

    this._analyticsInitPromise = (async () => {
      try {
        // ✅ Используем публичный endpoint без авторизации
        const response = await axios.get('/api/seo/public-analytics');

        if (response.data && response.data.success) {
          const { google_analytics_id, yandex_metrika_id, google_tag_manager_id } = response.data.data || {};

          if (google_tag_manager_id) {
            this.initGoogleAnalytics(google_tag_manager_id);
            if (IS_DEV) console.log('📊 Google Tag Manager (public-analytics):', google_tag_manager_id);
          } else if (google_analytics_id) {
            this.initGoogleAnalytics(google_analytics_id);
            if (IS_DEV) console.log('📊 Google Analytics (public-analytics):', google_analytics_id);
          }

          if (yandex_metrika_id) {
            this.initYandexMetrika(yandex_metrika_id);
            if (IS_DEV) console.log('📊 Яндекс.Метрика инициализирована:', yandex_metrika_id);
          }
        }
      } catch (error) {
        logSeoDevApiError('SEO: инициализация аналитики', error);
        // Не бросаем ошибку - аналитика не критична
      } finally {
        // Не даём повторно дергать init (даже если аналитика не критична и упала)
        this._analyticsInitialized = true;
      }
    })();

    return this._analyticsInitPromise;
  }

  /**
   * Дополнительная JSON-LD разметка для отдельных страниц (/legal, /trust).
   * Не удаляет script[data-seo-jsonld] из applyMeta.
   */
  injectJsonLd(objects, { marker = 'page' } = {}) {
    if (typeof document === 'undefined' || !Array.isArray(objects)) return;
    const attr = `data-seo-${marker}-jsonld`;
    document.querySelectorAll(`script[${attr}]`).forEach((n) => {
      if (n.parentNode) n.parentNode.removeChild(n);
    });
    objects.forEach((obj) => {
      if (!obj || typeof obj !== 'object') return;
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(attr, '1');
      script.text = JSON.stringify(obj);
      document.head.appendChild(script);
    });
  }

  removeJsonLd({ marker = 'page' } = {}) {
    if (typeof document === 'undefined') return;
    const attr = `data-seo-${marker}-jsonld`;
    document.querySelectorAll(`script[${attr}]`).forEach((n) => {
      if (n.parentNode) n.parentNode.removeChild(n);
    });
  }
}

// ✅ ИСПРАВЛЕНО: Экспортируем класс и создаём синглтон отдельно для избежания проблем с инициализацией
const seoServiceInstance = new SeoService();
export default seoServiceInstance;

